import axios from 'axios'
import { compile } from 'path-to-regexp'

import { VITE_API_PROXY_BASE_URL } from '@/features/api/constants.js'
import { createClient } from '@/features/api/createClient.js'
import { networkErrorInterceptor } from '@/features/api/networkErrorInterceptor.js'
import { tooMuchRequestInterceptor } from '@/features/api/tooMuchRequestInterceptor.js'
import { unauthorizedResponseInterceptor } from '@/features/api/unauthorizedResponseInterceptor.js'
import { getBearerToken, resolveRequestBearerToken } from '@/features/auth/auth.utils.js'
import { getActiveClubId } from '@/features/router/useActiveClub.js'

export const apiClient = createClient(VITE_API_PROXY_BASE_URL)
const publicApiClient = createClient(VITE_API_PROXY_BASE_URL)

export const tenantIdHeader = 'tenant-id'

apiClient.interceptors.request.use(function (config) {
  const clubId = getActiveClubId()

  const location = window.location
  const queryParams = new URLSearchParams(location.search)
  const queryClubId = queryParams.get('clubId')

  const requestClubId = queryClubId || clubId
  const token = resolveRequestBearerToken(config.authTokenMode, requestClubId)

  config.headers = {
    ...config.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(!config.skipTenantIdHeader && requestClubId ? { [tenantIdHeader]: requestClubId } : {}),
  }

  delete config.skipTenantIdHeader
  delete config.authTokenMode

  return config
})

export function setXTenantIdHeader(clubId) {
  apiClient.interceptors.request.use(function (config) {
    config.headers = {
      ...config.headers,
      ...(clubId ? { [tenantIdHeader]: clubId } : {}),
    }

    return config
  })
}

export function apiClientWithTenantIdHeader(clubId) {
  // Create a clone of the original client instead of modifying the original
  const clonedClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    timeout: apiClient.defaults.timeout,
  })

  const token = getBearerToken()

  clonedClient.interceptors.request.use(function (config) {
    config.headers = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(clubId ? { [tenantIdHeader]: clubId } : {}),
    }

    return config
  })

  return clonedClient
}

unauthorizedResponseInterceptor(apiClient)
tooMuchRequestInterceptor(apiClient)
networkErrorInterceptor(apiClient)

const defaultUrlParams = {}
const defaultQueryParams = {}
const defaultHeaders = {}

function urlCompiler(url, urlParams) {
  const compiler = compile(url)
  return compiler(urlParams)
}

async function handleRequest(method, config, client) {
  const {
    url,
    urlParams = defaultUrlParams,
    queryParams = defaultQueryParams,
    headers = defaultHeaders,
    data,
    responseType,
    signal,
    onUploadProgress,
    skipTenantIdHeader,
    authTokenMode,
  } = config
  const compiledUrl = urlCompiler(url, urlParams)

  return client.request({
    method,
    url: compiledUrl,
    params: queryParams,
    headers: headers,
    data,
    responseType,
    signal,
    skipTenantIdHeader,
    authTokenMode,
    onUploadProgress: function (progressEvent) {
      if (onUploadProgress && progressEvent.lengthComputable) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onUploadProgress(percentCompleted, progressEvent)
      }
    },
  })
}

export const Requester = {
  get(config) {
    return handleRequest('get', config, config.apiClient || apiClient)
  },

  post(config) {
    return handleRequest('post', config, config.apiClient || apiClient)
  },

  put(config) {
    return handleRequest('put', config, config.apiClient || apiClient)
  },

  delete(config) {
    return handleRequest('delete', config, config.apiClient || apiClient)
  },
}

export const PublicRequester = {
  get(config) {
    return handleRequest('get', config, config.apiClient || publicApiClient)
  },

  post(config) {
    return handleRequest('post', config, config.apiClient || publicApiClient)
  },

  put(config) {
    return handleRequest('put', config, config.apiClient || publicApiClient)
  },

  delete(config) {
    return handleRequest('delete', config, config.apiClient || publicApiClient)
  },
}

networkErrorInterceptor(publicApiClient)
